import {
  montosEnLinea,
  limpiarConcepto,
  round2,
  fechaDesdePartes,
  parsearMonto,
  crearFiltroPagina,
  armarDetalle,
} from "./helpers";

/**
 * Plantilla de BANBAJÍO (Banco del Bajío).
 *
 * Formato del "DETALLE DE LA CUENTA":
 *   FECHA | NO. REF./DOCTO | DESCRIPCION DE LA OPERACION | DEPOSITOS | RETIROS | SALDO
 *
 * Retos propios de BanBajío:
 *  - Estado de cuenta de MUCHAS páginas (18) con encabezados y pies repetidos por
 *    página; los movimientos van de "SALDO INICIAL" a "SALDO TOTAL".
 *  - Cada movimiento cabe en UNA línea (fecha + importe + saldo); las líneas
 *    siguientes son detalle del SPEI (INSTITUCIÓN, ORDENANTE, CLAVE DE RASTREO…) y
 *    se ignoran porque no traen importes.
 *  - Fecha "D MMM" (ej. "1 MAY"): sin año; el año sale del período del encabezado
 *    ("PERIODO: 1 DE MAYO ... DE 2026"), con anioRef como respaldo.
 *  - Columnas DEPOSITOS/RETIROS colapsadas al extraer texto y, ojo, BanBajío llama
 *    "CARGOS" a lo que SALE: por eso el tipo se deduce por la variación del saldo
 *    (sube = depósito/cargo, baja = retiro/abono), no por la palabra de la columna.
 *    (En el ERP cargo = depósito que entra, abono = pago que sale.)
 */

// Fecha al inicio del renglón: día + mes en español de 3 letras ("1 MAY", "31 MAY").
const RE_FECHA_INICIO = /^(\d{1,2})\s+(ENE|FEB|MAR|ABR|MAY|JUN|JUL|AGO|SEP|OCT|NOV|DIC)\b/i;
// Inicio y fin de la sección de movimientos.
const RE_INICIO = /detalle de la cuenta|saldo inicial/i;
const RE_FIN = /saldo\s+total|total de movimientos en el periodo/i;
// Saldo inicial que arranca la cadena de saldos. [^$\d]* salta el símbolo de moneda
// pegado al número (por si no es un "$" normal).
const RE_SALDO_INICIAL = /saldo\s+inicial\b[^$\d]*\$?\s*(-?\d{1,3}(?:,\d{3})*\.\d{2})/i;
// Primer importe del renglón, para cortar ahí el concepto.
const RE_PRIMER_MONTO = /-?\(?\$?\s?\d{1,3}(?:,\d{3})*\.\d{2}/;

const banbajio = {
  id: "banbajio",
  nombre: "BanBajío",
  clavesSat: ["030"],
  // Detección por identidad PROPIA de BanBajío. NO uso "banbajio" a secas porque
  // aparece como destino de un SPEI ("GJ FOOD BANBAJIO") en estados de OTROS bancos.
  detectar: (texto) => /banco del baj[ií]o|cuenta conecta banbaj[ií]o/i.test(texto),
  // Encabezado: "PERIODO: 1 DE MAYO AL 31 DE MAYO DE 2026" + "SALDO TOTAL* $ ...".
  extraerEncabezado(lineas) {
    const t = (lineas || []).join("\n");
    const per = t.match(
      /periodo:\s*(\d{1,2})\s+de\s+([a-záéíóú]+)\s+al\s+(\d{1,2})\s+de\s+([a-záéíóú]+)\s+de\s+(\d{4})/i
    );
    const sf = t.match(/saldo\s+total\b[^$\d]*\$?\s*(-?\d{1,3}(?:,\d{3})*\.\d{2})/i);
    return {
      fechaInicial: per ? fechaDesdePartes(per[1], per[2], per[5]) : null,
      fechaFinal: per ? fechaDesdePartes(per[3], per[4], per[5]) : null,
      saldoFinal: sf ? parsearMonto(sf[1]) : null,
    };
  },
  parsear(lineas, anioRef) {
    const arr = lineas || [];

    // Año del período (las fechas de renglón sólo traen "D MMM").
    const perTxt = arr
      .join("\n")
      .match(/periodo:\s*\d{1,2}\s+de\s+[a-záéíóú]+\s+al\s+\d{1,2}\s+de\s+[a-záéíóú]+\s+de\s+(\d{4})/i);
    const anioPeriodo = perTxt ? parseInt(perTxt[1], 10) : anioRef;

    // Acotar a la sección de movimientos (de "DETALLE DE LA CUENTA" a "SALDO TOTAL").
    let ini = arr.findIndex((l) => RE_INICIO.test(l));
    let fin = arr.length;
    for (let i = ini + 1; i < arr.length; i++) {
      if (RE_FIN.test(arr[i])) { fin = i; break; }
    }
    const seccion = arr.slice(ini + 1, fin);

    // Saldo inicial para arrancar la cadena de saldos y deducir el tipo por delta.
    let prevSaldo = null;
    for (const l of arr) {
      const m = l.match(RE_SALDO_INICIAL);
      if (m) { prevSaldo = parsearMonto(m[1]); break; }
    }

    // Cada línea con fecha es un movimiento; las siguientes (INSTITUCIÓN, ORDENANTE,
    // CLAVE DE RASTREO…) son su detalle, sin los encabezados/pies de cada hoja.
    const idxPrimerMov = seccion.findIndex((l) => RE_FECHA_INICIO.test(l));
    const esDePagina = crearFiltroPagina(arr, idxPrimerMov > -1 ? ini + 1 + idxPrimerMov : -1);
    const registros = [];
    for (const linea of seccion) {
      if (RE_FECHA_INICIO.test(linea)) registros.push({ linea, extras: [] });
      else if (registros.length && !esDePagina(linea)) registros[registros.length - 1].extras.push(linea);
    }

    const movimientos = [];
    for (const { linea, extras } of registros) {
      const mFecha = linea.match(RE_FECHA_INICIO);

      const ms = montosEnLinea(linea);
      if (ms.length < 2) continue; // necesita importe + saldo en el mismo renglón

      const fecha = fechaDesdePartes(mFecha[1], mFecha[2], anioPeriodo);
      // Importe = PRIMER monto, saldo = ÚLTIMO. Robusto por si pdf.js duplica el
      // renglón ([importe, importe, saldo, saldo]) como en Santander.
      const saldo = ms[ms.length - 1];
      const monto = Math.abs(ms[0]);

      // Tipo por variación del saldo (depósito = cargo, retiro = abono).
      let tipo = null;
      if (prevSaldo != null) {
        tipo = round2(saldo - prevSaldo) >= 0 ? "cargo" : "abono";
      }
      prevSaldo = saldo;

      // Concepto: texto entre la fecha y el primer importe del renglón.
      const idxMonto = linea.search(RE_PRIMER_MONTO);
      const concepto = limpiarConcepto(
        linea.slice(mFecha[0].length, idxMonto > -1 ? idxMonto : undefined)
      );

      movimientos.push({
        fecha,
        monto: round2(monto),
        tipo,
        concepto,
        detalle: armarDetalle([concepto, ...extras]),
        saldo,
        raw: linea,
        rawExtra: extras.join(" "),
      });
    }

    return movimientos;
  },
};

export default banbajio;
